import { slugify } from 'helpers/string';

export type ArticleHeading = { id: string; text: string };

const H2_PATTERN = /<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/gi;
const WORDS_PER_MINUTE = 225;

const headingText = (inner: string): string =>
  inner
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&(?:#39|apos|rsquo|lsquo);/g, "'")
    .replace(/&(?:quot|ldquo|rdquo);/g, '"')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Gives every <h2> an id so the contents list and search engines can jump straight to it. */
export const addHeadingAnchors = (
  html: string
): { html: string; headings: ArticleHeading[] } => {
  const headings: ArticleHeading[] = [];
  const used = new Set<string>();

  const anchored = html.replace(
    H2_PATTERN,
    (match: string, attrs: string | undefined, inner: string) => {
      const text = headingText(inner);
      const base = slugify(text).replace(/^-+|-+$/g, '') || 'section';
      let id = base;

      for (let n = 2; used.has(id); n++) {
        id = `${base}-${n}`;
      }

      used.add(id);
      headings.push({ id, text });

      return /\sid=/.test(attrs || '')
        ? match
        : `<h2 id="${id}"${attrs || ''}>${inner}</h2>`;
    }
  );

  return { html: anchored, headings };
};

export const estimateReadingMinutes = (html: string): number => {
  const words = html
    .replace(/<[^>]+>/g, ' ')
    .split(/\s+/)
    .filter(Boolean).length;

  return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
};
